import React, { useEffect } from "react"
import Grid from '@mui/material/Grid';
import OutlinedInput from '@mui/material/OutlinedInput';
import { Select, MenuItem, Paper, Button, IconButton } from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import SpeedDial from '@mui/material/SpeedDial';
import { useClientsManagementController } from "./context/clients-management-controller";
import { useClientsManagementState } from "./context/clients-management-context";
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DefaultDrawer from "../../components/default-drawer";
import ClientsManagementForm from "./components/clients-management-form";
import MaskedTextField from "../../components/masked-text-field";

export const ClientsManagement = () => {
    const {
        handleFilterChange,
        clearFilters,
        loadData,
        onChangePagination,
        searchClients,
        openDrawer,
        closeDrawer,
        saveClient
    } = useClientsManagementController();

    const {
        clientsPagedRequest,
        clientsPagedResponse,
        isLoadingData,
        isDrawerOpen,
        isEditing
    } = useClientsManagementState();

    useEffect(() => {
        (async () => {
            await loadData(clientsPagedRequest);
        })();
    }, []);

    const columns: GridColDef[] = [
        {
            field: 'id',
            headerName: 'Id',
            flex: 0
        },
        {
            field: 'name',
            headerName: 'Nome',
            flex: 1,
            minWidth: 200,
        },
        {
            field: 'corporateName',
            headerName: 'Razão Social',
            flex: 1,
            minWidth: 200,
        },
        {
            field: 'formattedDocument',
            headerName: 'CNPJ',
            width: 150,
        },
        {
            field: 'entityTypeName',
            headerName: 'Tipo',
            width: 120,
        },
        {
            field: 'actions',
            headerName: 'Ações',
            width: 100,
            sortable: false,
            renderCell: (params) => (
                <IconButton
                    size="small"
                    onClick={() => openDrawer(true, params.row.id)}
                >
                    <EditIcon />
                </IconButton>
            ),
        },
    ];

    // const handlePaginationModelChange = (model: GridPaginationModel) => {
    //     onChangePagination(model.page, model.pageSize);
    // };

    return (
        <React.Fragment>

            <Grid item xs={12} md={12} lg={12}>
                <Paper
                    sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <InputLabel htmlFor="input-name">Nome</InputLabel>
                            <OutlinedInput
                                id="input-name"
                                name="input-name"
                                value={clientsPagedRequest?.name || ''}
                                onChange={handleFilterChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <InputLabel htmlFor="input-document">CNPJ</InputLabel>
                            <MaskedTextField
                                maskType="cnpj"
                                size="small"
                                id="input-document"
                                name="input-document"
                                value={clientsPagedRequest?.document || ''}
                                onChange={handleFilterChange}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <InputLabel htmlFor="select-entity-type">Tipo de Cliente</InputLabel>
                            <Select
                                id="select-entity-type"
                                name="select-entity-type"
                                value={clientsPagedRequest?.entityType || ''}
                                onChange={handleFilterChange}
                                fullWidth
                                size="small"
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value={1}>Prefeituras Municipais</MenuItem>
                                <MenuItem value={2}>Câmaras Municipais</MenuItem>
                            </Select>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
                                <Button
                                    variant="contained"
                                    startIcon={<SearchIcon />}
                                    fullWidth
                                    onClick={searchClients}
                                    size="small"
                                >
                                    Buscar
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<ClearIcon />}
                                    fullWidth
                                    onClick={clearFilters}
                                    size="small"
                                >
                                    Limpar
                                </Button>
                            </div>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
                <Paper style={{ height: 600, width: '100%' }}>
                    <DataGrid
                        rows={clientsPagedResponse?.items || []}
                        columns={columns}
                        loading={isLoadingData}
                        paginationMode="server"
                        pagination={true}
                        sx={{
                            '& .MuiDataGrid-row': {
                                cursor: 'pointer',
                            },
                            '.MuiDataGrid-virtualScroller': {
                                overflowY: 'auto !important',
                                // example of maxHeight
                                maxHeight: '50vh !important',
                            },
                        }}
                        localeText={{
                            noRowsLabel: "Não foi encontrado resultados",
                            MuiTablePagination: {
                                labelDisplayedRows: ({ from, to, count, page }) =>
                                    `Página ${Number(page) === 0 ? 1 : Number(page) + 1} de ${count === 0 || clientsPagedResponse.pageSize === 0 ? 1 : Math.ceil(count / clientsPagedResponse.pageSize)} | Total de Registros: ${clientsPagedResponse.totalRecords}`,
                            }
                        }}
                        hideFooterSelectedRowCount
                        disableRowSelectionOnClick
                        pageSizeOptions={[]}
                        paginationModel={{ page: clientsPagedRequest?.page, pageSize: clientsPagedRequest?.pageSize }}
                        rowCount={clientsPagedResponse?.totalRecords || 0}
                        onPaginationModelChange={(e) => {
                            onChangePagination(e.page, e.pageSize);
                        }}
                    />
                </Paper>

                <SpeedDial
                    ariaLabel="Adicionar Cliente"
                    sx={{ position: 'fixed', bottom: 16, right: 16 }}
                    icon={<AddIcon />}
                    onClick={() => openDrawer(false)}
                />

                <DefaultDrawer
                    isOpen={isDrawerOpen}
                    onClose={closeDrawer}
                    title={isEditing ? "Editar Cliente" : "Novo Cliente"}
                    actions={() => {
                        return <Button
                            variant="contained"
                            onClick={saveClient}
                        >
                            Salvar
                        </Button>;
                    }}>
                    <ClientsManagementForm />
                </DefaultDrawer>
            </Grid>
        </React.Fragment>
    );
};

export default ClientsManagement;